import { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Typography,
  Space,
  Card,
} from "antd";
import {
  BankOutlined,
  MailOutlined,
} from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import {
  isPossiblePhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import "react-phone-input-2/lib/style.css";
import { useAppDispatch } from "../../../../hooks/reduxHooks";
import {
  getBusinessInfoApi,
} from "../../../../services/tenantSetup.service";
import { getStepData, updateStepData } from "../../../../utils/tenantSetupStorage";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const Step1OrganizationSetup = ({ initialData, onSubmit, loading }) => {
  const [form] = Form.useForm();

  const [phoneInputValue, setPhoneInputValue] = useState("");
  const sessionDataLoadedRef = useRef(false);
  const dispatch = useAppDispatch();
  // Utility function to parse phone number and extract country code
  const parsePhoneNumberData = (phoneNumber) => {
    if (!phoneNumber) {
      return {
        phone_input: "",
        country_code: "",
        phone_number: "",
      };
    }

    try {
      // If the phone number doesn't start with +, add it
      const formattedPhone = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+${phoneNumber}`;

      // Parse the phone number
      const parsed = parsePhoneNumber(formattedPhone);

      if (parsed && parsed.country && parsed.nationalNumber) {
        return {
          phone_input: phoneNumber, // Keep original format for PhoneInput
          country_code: `+${parsed.countryCallingCode}`,
          phone_number: parsed.nationalNumber,
        };
      }
    } catch (error) {
      // Error parsing phone number, will use fallback
    }

    // Fallback - return the original number as phone_input
    return {
      phone_input: phoneNumber,
      country_code: "",
      phone_number: phoneNumber.replace(/^\+/, ""), // Remove + if present for fallback
    };
  };



  useEffect(() => {
    dispatch(
      getBusinessInfoApi({
        finalCallback: () => { },
        successCallback: (response) => {
          if (sessionDataLoadedRef.current) return;
          // Populate form from business info API
          let formData = {
            org_name: response.org_name || "",
            org_description: response.org_description || "",
            org_email: response.org_email || "",
          };

          // Handle phone
          if (response.contact_number) {
            const phoneData = parsePhoneNumberData(response.contact_number);
            formData = { ...formData, ...phoneData };
            setPhoneInputValue(response.contact_number);
          }

          form.setFieldsValue(formData);
        },
        failureCallback: () => { },
      })
    );
  }, [dispatch, form]);

  useEffect(() => {
    if (sessionDataLoadedRef.current) return;
    if (initialData) {
      let formInitialValues = {
        ...initialData,
      };

      // Handle org_contact field from initialData if present
      if (initialData.org_contact && !initialData.phone_input) {
        const phoneData = parsePhoneNumberData(initialData.org_contact);
        formInitialValues = {
          ...formInitialValues,
          ...phoneData,
        };
        setPhoneInputValue(initialData.org_contact);
      } else {
        // Use existing phone data if available
        formInitialValues = {
          ...formInitialValues,
          country_code: initialData.country_code || "",
          phone_number: initialData.phone_number || "",
          phone_input: initialData.phone_input || "",
        };

        // Set phone input value state to ensure PhoneInput displays correctly
        if (initialData.phone_input) {
          setPhoneInputValue(initialData.phone_input);
        }
      }

      form.setFieldsValue(formInitialValues);
    }
  }, [initialData, form]);

  // Load step data from session storage on mount
  useEffect(() => {
    const savedStepData = getStepData("step1");
    if (savedStepData && Object.keys(savedStepData).length > 0) {
      let formValues = { ...savedStepData };

      if (savedStepData.org_contact && !savedStepData.phone_input) {
        const phoneData = parsePhoneNumberData(savedStepData.org_contact);
        formValues = { ...formValues, ...phoneData };
        setPhoneInputValue(savedStepData.org_contact);
      } else if (savedStepData.phone_input) {
        setPhoneInputValue(savedStepData.phone_input);
      }

      form.setFieldsValue(formValues);
      sessionDataLoadedRef.current = true;
    }
  }, [form]);

  useEffect(() => {
    // getBusinessInfoApi call this and get the business data and populate the form
    dispatch(
      getBusinessInfoApi({
        finalCallback: () => { },
        successCallback: (response) => {
          if (sessionDataLoadedRef.current) return;
          // Parse contact_number to populate phone fields
          if (response.contact_number) {
            const phoneNumber = response.contact_number;
            const phoneData = parsePhoneNumberData(phoneNumber);

            // Set the phone input value for the PhoneInput component
            setPhoneInputValue(phoneNumber);

            // Parse and set form values with proper phone field mapping
            const formData = {
              ...response,
              ...phoneData,
            };

            form.setFieldsValue(formData);
          } else {
            form.setFieldsValue(response);
          }
        },
        failureCallback: () => { },
      })
    );
  }, [dispatch, form]);

  const handleSubmit = (values) => {
    // Prepare data for Step 1: Organization Setup
    const formData = {
      org_name: values.org_name,
      org_email: values.org_email,
      org_description: values.org_description,
      org_goal: values.org_description, // Same value as org_description
      org_contact: values.phone_input || "", // Use full formatted phone number
    };

    updateStepData("step1", { ...form.getFieldsValue(), ...formData });
    onSubmit(formData);
  };

  const handleValuesChange = (_, allValues) => {
    updateStepData("step1", allValues);
  };

  return (
    <div className="step1-organization-setup">
      <div className="step1-organization-setup__header">
        <Title
          level={2}
          style={{ marginBottom: 8, color: "#262626", textAlign: "center" }}
        >
          Organization Setup
        </Title>
        <Paragraph
          style={{
            textAlign: "center",
            fontSize: "15px",
            color: "#8c8c8c",
            marginBottom: 24,
          }}
        >
          Tell us about your organization and get started
        </Paragraph>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        disabled={loading}
        initialValues={{
          ...initialData,
          // Handle phone number defaults - if org_contact exists but phone_input doesn't, parse it
          ...(initialData?.org_contact && !initialData?.phone_input
            ? parsePhoneNumberData(initialData.org_contact)
            : {
              country_code: initialData?.country_code || "",
              phone_number: initialData?.phone_number || "",
              phone_input: initialData?.phone_input || "",
            }),
        }}
        size="large"
        requiredMark={false}
      >
        {/* Organization Information Section */}
        <Card
          title={
            <Space>
              <BankOutlined style={{ color: "#1890ff", fontSize: 18 }} />
              <Text strong style={{ fontSize: 18 }}>
                Organization Information
              </Text>
            </Space>
          }
          className="form-section-card"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 20]} style={{ padding: 24 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="org_name"
                label={
                  <span>
                    Organization Name <span style={{ color: "red" }}>*</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter your organization name",
                  },
                ]}
              >
                <Input
                  prefix={<BankOutlined style={{ color: "#8c8c8c" }} />}
                  placeholder="Enter your organization name"
                  className="professional-input"
                />
              </Form.Item>
            </Col>


            <Col xs={24} sm={12}>
              <Form.Item
                name="phone_input"
                label={
                  <span>
                    Contact Number
                  </span>
                }
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const phoneToValidate = value.startsWith("+")
                        ? value
                        : `+${value}`;
                      return isPossiblePhoneNumber(phoneToValidate)
                        ? Promise.resolve()
                        : Promise.reject(
                          new Error("Please enter a valid phone number")
                        );
                    },
                  },
                ]}
              >
                <style>
                  {`
                    .country-search {
                      padding: 0 !important;
                      margin: 0;
                      height: 40px;               /* or your desired height */
                      display: flex;
                      align-items: center;
                    }

                    .country-search-box {
                      width: 100%;
                      height: 100%;
                      // padding: 0 12px;
                      border: none;
                      outline: none;
                      box-sizing: border-box;
                      margin-left: 0 !important;

                      /* Optional */
                      background: transparent;
                    }
                  `}
                </style>
                <PhoneInput
                  country={"np"}
                  enableSearch={true}
                  inputClass="form-input"
                  containerClass="phone-input-container"
                  buttonClass="country-dropdown"
                  searchClass="country-search"
                  dropdownClass="country-dropdown-list"
                  disabled={loading}
                  value={
                    phoneInputValue || form.getFieldValue("phone_input") || ""
                  }
                  inputStyle={{
                    width: "100%",
                    height: "40px",
                    fontSize: "14px",
                    fontWeight: "400",
                    borderRadius: "0 8px 8px 0",
                    backgroundColor: loading ? "#f5f5f5" : "#ffffff",
                    color: loading ? "#00000040" : "#000000",
                    cursor: loading ? "not-allowed" : "text",
                  }}
                  buttonStyle={{
                    borderRadius: "8px 0 0 8px",
                    borderRight: "none",
                    backgroundColor: loading ? "#f5f5f5" : "#fafafa",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  onChange={(phone, countryData) => {
                    setPhoneInputValue(phone);
                    const countryCode = countryData.dialCode
                      ? `+${countryData.dialCode}`
                      : "";
                    const fullNumber = phone;
                    const phoneNumberOnly = countryData.dialCode
                      ? fullNumber.replace(countryData.dialCode, "")
                      : fullNumber;
                    const cleanedPhoneNumber = phoneNumberOnly.replace(
                      /\D/g,
                      ""
                    );

                    form.setFieldsValue({
                      country_code: countryCode,
                      phone_number: cleanedPhoneNumber,
                      phone_input: phone,
                    });
                  }}
                  preferredCountries={["in", "us", "np", "gb"]}
                  autoFormat={true}
                  placeholder="Enter your contact number"
                  disableSearchIcon={true}
                  searchPlaceholder="Search country"
                />
              </Form.Item>

              {/* Hidden fields for country code and phone number */}
              <Form.Item name="country_code" hidden={true}>
                <Input />
              </Form.Item>

              <Form.Item name="phone_number" hidden={true}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="org_email"
                label={
                  <span>
                    Email
                  </span>
                }
                rules={[
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input
                  type="email"
                  prefix={<MailOutlined style={{ color: "#8c8c8c" }} />}
                  placeholder="Enter your organization email"
                  className="professional-input"
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="org_description"
                label={
                  <span>
                    Organization Description{" "}
                  </span>
                }
              >
                <TextArea
                  placeholder="Briefly describe what your organization does..."
                  rows={4}
                  className="professional-textarea"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </div>
  );
};

export default Step1OrganizationSetup;
